import { Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { App } from '@df/core/app';
import last from 'lodash-es/last';
import { TimeoutService } from '@df/ng/timeout.service';
import { OnInteractionService } from '@df/ui/common/on-interaction.service';
import { getElementHeight } from '@df/dom/fn/get-element-height';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { fromEvent, merge, Subscription } from 'rxjs';
/**
 * ## sample usage
 *
 * ```html
 * <div (infiniteScroll)="listing.increaseLimit()"
 *       [infinitScrollActive]="listing.getLimit() < listing.getTotal()">
 *              ...
 * </div>
 * ```
 */

@Directive({
    selector: '[infiniteScroll]',
    standalone: true
})
export class InfiniteScrollDirective implements OnInit, OnDestroy, OnChanges {
    /**
     * Action
     */
    @Output()
    infiniteScroll = new EventEmitter();

    /**
     * Set this when the list is inside the scrollable container
     * in that case do put the directive on that container
     */
    @Input()
    infiniteScrollUseContainer: boolean = false;

    /**
     * Flag to manage if directive is active
     */
    @Input()
    infinitScrollActive: boolean = true;

    /**
     * Offset in px below what we trigger action
     */
    @Input()
    infinitScrollOffset: number = 2000; // after interaction one, initial one is like 20px

    private intersectionObserver?: IntersectionObserver;

    /**
     * If cutout is visible now
     */
    private isVisible = false;

    /**
     * Recheck timeout
     */
    private recheckInterval?: number;
    /**
     * Viewport changes subscription
     */
    private subscription?: Subscription;
    private interactionSubscription?: Subscription;

    check: () => void;

    /**
     * Class constructor
     * @param  {ElementRef} private _elementRef
     */
    constructor(
        private elementRef: ElementRef,
        private renderer2: Renderer2,
        private app: App,
        private timeoutService: TimeoutService,
        private onInteractionService: OnInteractionService
    ) {
        this.check = timeoutService.throttle(() => this._check(), 300, { leading: false, trailing: true });
    }

    /**
     * Initial hook subscribed to viewport changes
     */
    ngOnInit() {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        if (this.infiniteScrollUseContainer) {
            this.subscription = merge(
                fromEvent(this.elementRef.nativeElement, 'scroll'),
                fromEvent(this.elementRef.nativeElement, 'touchmove')
            ).subscribe(() => this.check());
        } else {
            const bottomElement = this.renderer2.createElement('div');
            this.renderer2.appendChild(this.elementRef.nativeElement, bottomElement);
            this.intersectionObserver?.disconnect();
            this.interactionSubscription = this.onInteractionService.scrollOnCurrentInteraction.subscribe(interacted => {
                this.intersectionObserver = new IntersectionObserver(
                    entries => {
                        this.isVisible = !!last(entries)?.isIntersecting;
                        this.apply();
                    },
                    {
                        root: this.infiniteScrollUseContainer ? this.elementRef.nativeElement : null,
                        threshold: 1,
                        rootMargin: `0px 0px ${interacted ? this.infinitScrollOffset : 20}px 0px`
                    }
                );
                this.intersectionObserver.observe(bottomElement);
            });
        }
    }

    ngOnChanges(changes) {
        if (this.infinitScrollActive && changes.infinitScrollActive) {
            this.apply();
        }
    }

    /**
     * Apply event if needed
     */
    private apply() {
        clearInterval(this.recheckInterval);
        if (this.infinitScrollActive && this.isVisible) {
            this.infiniteScroll.emit();
            // keep emiting every 1s till we are not intersecting
            this.recheckInterval = this.timeoutService.setInterval(() => {
                if (this.infinitScrollActive && this.isVisible) {
                    this.infiniteScroll.emit();
                } else {
                    clearInterval(this.recheckInterval);
                }
            }, 500);
        }
    }

    /**
     * Final hook to cleanup
     */
    ngOnDestroy() {
        this.intersectionObserver?.disconnect();
        this.subscription?.unsubscribe();
        this.interactionSubscription?.unsubscribe();
    }
    /**
     * On scroll event
     * Final hook to cleanup
     */
    _check() {
        if (this.infinitScrollActive) {
            if (this.infiniteScrollUseContainer) {
                const element = this.elementRef.nativeElement;
                if (element.scrollTop + this.infinitScrollOffset + getElementHeight(element) >= element.scrollHeight) {
                    this.infiniteScroll.emit();
                }
            } else {
                if (isInViewport(this.elementRef.nativeElement, this.infinitScrollOffset, 'bottom')) {
                    this.infiniteScroll.emit();
                }
            }
        }
    }
}
